import React, { useState, useEffect } from 'react';
import { X, Briefcase, UserPlus, Search, Loader2, MapPin } from 'lucide-react';
import projectsService from '../services/projectsService';
import projectHoursService from '../services/projectHoursService';
import { clientService } from '../services/api';
import toast from 'react-hot-toast';
import QuickClientModal from './QuickClientModal';
import ProjectHoursForm from './ProjectHoursForm.jsx';

const ManualProjectModal = ({ onClose, onRefresh }) => {
    const [loading, setLoading] = useState(false);
    const [loadingClients, setLoadingClients] = useState(false);
    const [loadingCep, setLoadingCep] = useState(false);
    const [clients, setClients] = useState([]);
    const [showQuickClient, setShowQuickClient] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [projectHours, setProjectHours] = useState([]);

    const [formData, setFormData] = useState({
        client_id: '',
        client_name: '',
        client_apelido: '',
        client_cpf: '',
        client_phone: '',
        client_email: '',
        project_name: '',
        description: '',
        project_type: 'custom',
        project_size: '',
        priority: 'NORMAL',
        estimated_budget: '',
        estimated_start_date: '',
        estimated_completion_date: '',
        estimated_production_hours: '',
        estimated_assembly_hours: ''
    });

    const [addressFields, setAddressFields] = useState({
        cep: '',
        street: '',
        number: '',
        complement: '',
        neighborhood: '',
        city: '',
        state: ''
    });

    useEffect(() => {
        fetchClients();
    }, []);

    const fetchClients = async (search = '') => {
        try {
            setLoadingClients(true);
            const response = await clientService.getClients({ search, limit: 50 });
            setClients(response.data.data || []);
        } catch (error) {
            console.error('Erro ao buscar clientes:', error);
        } finally {
            setLoadingClients(false);
        }
    };

    const maskCep = (value) => {
        if (!value) return '';
        const numbers = value.replace(/\D/g, '');
        return numbers.replace(/(\d{5})(\d)/, '$1-$2').replace(/(-\d{3})\d+?$/, '$1');
    };

    const fetchCep = async (cep) => {
        const cleanCep = cep.replace(/\D/g, '');
        if (cleanCep.length !== 8) {
            toast.error('CEP deve conter 8 dígitos');
            return;
        }

        setLoadingCep(true);
        try {
            const apiUrl = process.env.REACT_APP_API_URL || '/api';
            const response = await fetch(`${apiUrl}/utils/cep/${cleanCep}`);
            const data = await response.json();

            if (data.erro) {
                toast.error('CEP não encontrado');
                return;
            }

            setAddressFields(prev => ({
                ...prev,
                street: data.logradouro || '',
                neighborhood: data.bairro || '',
                city: data.localidade || '',
                state: data.uf || '',
                complement: data.complemento || ''
            }));

            toast.success('CEP encontrado!');
        } catch (error) {
            console.error('Erro ao buscar CEP:', error);
            toast.error('Erro ao buscar CEP');
        } finally {
            setLoadingCep(false);
        }
    };

    const parseAddress = (addrString) => {
        if (!addrString) return null;

        // Padrão esperado: Rua, nº Número, Complemento, Bairro, Cidade, UF, CEP: 00000-000
        const parts = addrString.split(',').map(p => p.trim());
        const result = {
            cep: '',
            street: '',
            number: '',
            complement: '',
            neighborhood: '',
            city: '',
            state: ''
        };

        parts.forEach(part => {
            if (part.toLowerCase().includes('cep:')) {
                result.cep = part.split(':')[1]?.trim() || '';
            } else if (part.toLowerCase().startsWith('nº')) {
                result.number = part.replace(/nº/i, '').trim();
            } else if (part.length === 2 && part === part.toUpperCase() && !result.state) {
                // UF (geralmente 2 letras maiúsculas)
                result.state = part;
            }
        });

        // Tentar mapear por posição se houver muitas partes
        if (parts.length >= 3) {
            result.street = parts[0];

            // Se a última parte for CEP, vamos olhar as anteriores
            const lastIndex = parts.length - 1;
            const hasCepAtEnd = parts[lastIndex].toLowerCase().includes('cep:');

            if (parts.length === 5) { // Rua, Num, Bairro, Cidade, UF
                result.neighborhood = parts[2];
                result.city = parts[3];
                result.state = parts[4];
            } else if (parts.length === 6) {
                if (hasCepAtEnd) { // Rua, Num, Bairro, Cidade, UF, CEP
                    result.neighborhood = parts[2];
                    result.city = parts[3];
                    result.state = parts[4];
                } else { // Rua, Num, Complemento, Bairro, Cidade, UF
                    result.complement = parts[2];
                    result.neighborhood = parts[3];
                    result.city = parts[4];
                    result.state = parts[5];
                }
            } else if (parts.length === 7) { // Rua, Num, Cmpl, Bairro, Cidad, UF, CEP
                result.complement = parts[2];
                result.neighborhood = parts[3];
                result.city = parts[4];
                result.state = parts[5];
            }
        }

        // Caso de fallback: se não conseguiu mapear bem, apenas coloca tudo no street
        if (!result.street) result.street = addrString;

        return result;
    };

    const handleClientSelect = (client) => {
        setFormData(prev => ({
            ...prev,
            client_id: client.id,
            client_name: client.full_name || client.name || '',
            client_apelido: client.apelido || client.nickname || '',
            client_cpf: client.cpf || client.cpf_cnpj || '',
            client_phone: client.phone || client.telefone || '',
            client_email: client.email || '',
        }));

        if (client.address) {
            const parsed = parseAddress(client.address);
            if (parsed) {
                setAddressFields(parsed);
            } else {
                setAddressFields(prev => ({ ...prev, street: client.address }));
            }
        }

        setSearchTerm('');
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleAddressFieldChange = (field, value) => {
        setAddressFields(prev => ({ ...prev, [field]: value }));
    };

    const buildFullAddress = () => {
        const parts = [];
        if (addressFields.street) parts.push(addressFields.street);
        if (addressFields.number) parts.push(`nº ${addressFields.number}`);
        if (addressFields.complement) parts.push(addressFields.complement);
        if (addressFields.neighborhood) parts.push(addressFields.neighborhood);
        if (addressFields.city) parts.push(addressFields.city);
        if (addressFields.state) parts.push(addressFields.state);
        if (addressFields.cep) parts.push(`CEP: ${addressFields.cep}`);

        return parts.join(', ');
    };

    const handleHoursChange = (hours) => {
        setProjectHours(hours);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const fullAddress = buildFullAddress();

        // Validar CPF ou CNPJ (opcional - se fornecido, deve ter 11 ou 14 dígitos)
        const docClean = (formData.client_cpf || '').replace(/\D/g, '');
        if (formData.client_cpf && docClean.length !== 11 && docClean.length !== 14) {
            toast.error('CPF (11 dígitos) ou CNPJ (14 dígitos) inválido');
            return;
        }

        // Validar apelido
        if (!formData.client_apelido || formData.client_apelido.trim().length === 0) {
            toast.error('Apelido do cliente é obrigatório');
            return;
        }

        if (!formData.client_name || !fullAddress || !formData.project_name?.trim()) {
            toast.error('Por favor, preencha os campos obrigatórios (Cliente, Endereço e Nome do Projeto)');
            return;
        }

        try {
            setLoading(true);

            const resolvedProjectName = formData.project_name.trim();

            // Criar o projeto primeiro
            const projectResponse = await projectsService.createManualProject({
                ...formData,
                project_name: resolvedProjectName,
                client_cpf: formData.client_cpf || null,
                client_apelido: formData.client_apelido,
                client_address: fullAddress
            });

            const newProject = projectResponse.data;

            // Se houver horas definidas, salvar após criar o projeto
            const hoursWithData = projectHours.filter(h => parseFloat(h.hours) > 0);
            if (hoursWithData.length > 0) {
                await projectHoursService.saveProjectHours(newProject.id, hoursWithData);
            }

            toast.success('Projeto manual criado com sucesso!');
            onRefresh();
            onClose();
        } catch (error) {
            console.error('Erro ao criar projeto manual:', error);
            toast.error(error.response?.data?.message || 'Erro ao criar projeto manual');
        } finally {
            setLoading(false);
        }
    };

    const filteredClients = clients.filter(c =>
        c.full_name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col border border-secondary-200">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-secondary-100 bg-secondary-50/50">
                    <div className="flex items-center gap-3">
                        <div className="bg-primary-100 p-2 rounded-lg">
                            <Briefcase className="w-5 h-5 text-primary-700" />
                        </div>
                        <div>
                            <h2 className="text-lg font-bold text-secondary-900">
                                Novo Projeto Manual
                            </h2>
                            <p className="text-xs text-secondary-500">
                                Inserir projeto diretamente no sistema
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-secondary-400 hover:text-secondary-600 hover:bg-secondary-100 rounded-full transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Seção Cliente */}
                        <div className="space-y-4 md:col-span-2">
                            <div className="flex items-center justify-between">
                                <h3 className="text-xs font-semibold uppercase tracking-wider text-secondary-500 flex items-center gap-2">
                                    <span className="w-8 h-px bg-secondary-200"></span>
                                    Informações do Cliente
                                </h3>
                                <button
                                    type="button"
                                    onClick={() => setShowQuickClient(true)}
                                    className="text-primary-600 hover:text-primary-700 text-xs font-bold flex items-center gap-1 bg-primary-50 px-2 py-1 rounded-md transition-colors"
                                >
                                    <UserPlus className="w-3.5 h-3.5" />
                                    Novo Cliente
                                </button>
                            </div>

                            {/* Selector de Cliente */}
                            <div className="relative">
                                <label className="text-xs font-medium text-secondary-700 mb-1 block">Selecionar Cliente Cadastrado</label>
                                <div className="relative group">
                                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400" />
                                    <input
                                        type="text"
                                        placeholder="Buscar cliente..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="input pl-9 mb-1"
                                    />
                                    {searchTerm && (
                                        <div className="absolute z-10 w-full bg-white border border-secondary-200 rounded-md shadow-lg max-h-48 overflow-y-auto mt-1">
                                            {loadingClients ? (
                                                <div className="p-3 text-center text-secondary-500 text-sm flex items-center justify-center gap-2">
                                                    <Loader2 className="w-4 h-4 animate-spin" />
                                                    Carregando...
                                                </div>
                                            ) : filteredClients.length > 0 ? (
                                                filteredClients.map(client => (
                                                    <button
                                                        key={client.id}
                                                        type="button"
                                                        onClick={() => handleClientSelect(client)}
                                                        className="w-full text-left px-4 py-2 hover:bg-secondary-50 text-sm transition-colors border-b border-secondary-100 last:border-0"
                                                    >
                                                        <span className="font-semibold block">{client.full_name}</span>
                                                        <span className="text-xs text-secondary-500">{client.phone || client.email || 'Sem contato'}</span>
                                                    </button>
                                                ))
                                            ) : (
                                                <div className="p-3 text-center text-secondary-500 text-sm">
                                                    Nenhum cliente encontrado
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-1">
                                    <label className="text-xs font-medium text-secondary-700">Nome do Cliente *</label>
                                    <input
                                        type="text"
                                        name="client_name"
                                        value={formData.client_name}
                                        onChange={handleChange}
                                        className="input"
                                        placeholder="Ex: João Silva"
                                        required
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs font-medium text-secondary-700">Apelido *</label>
                                    <input
                                        type="text"
                                        name="client_apelido"
                                        value={formData.client_apelido}
                                        onChange={handleChange}
                                        className="input"
                                        placeholder="Como o cliente será chamado"
                                        required
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs font-medium text-secondary-700">CPF/CNPJ</label>
                                    <input
                                        type="text"
                                        name="client_cpf"
                                        value={formData.client_cpf}
                                        onChange={handleChange}
                                        className="input"
                                        placeholder="000.000.000-00"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs font-medium text-secondary-700">Telefone</label>
                                    <input
                                        type="text"
                                        name="client_phone"
                                        value={formData.client_phone}
                                        onChange={handleChange}
                                        className="input"
                                        placeholder="(11) 99999-9999"
                                    />
                                </div>
                            </div>

                            {/* Endereço Detalhado */}
                            <div className="space-y-4 pt-2 border-t border-secondary-100 mt-4">
                                <div className="flex items-center gap-2 text-secondary-900 font-bold text-sm">
                                    <MapPin className="w-4 h-4" />
                                    Endereço do Projeto *
                                </div>

                                <div className="space-y-1">
                                    <label className="text-xs font-medium text-secondary-700 block">CEP</label>
                                    <div className="flex gap-2">
                                        <div className="relative flex-1">
                                            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-secondary-400" />
                                            <input
                                                type="text"
                                                value={addressFields.cep}
                                                onChange={(e) => setAddressFields(prev => ({ ...prev, cep: maskCep(e.target.value) }))}
                                                onBlur={() => addressFields.cep.replace(/\D/g, '').length === 8 && fetchCep(addressFields.cep)}
                                                className="input pl-9"
                                                placeholder="00000-000"
                                                maxLength={9}
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => fetchCep(addressFields.cep)}
                                            disabled={loadingCep || addressFields.cep.replace(/\D/g, '').length !== 8}
                                            className="px-4 py-2 bg-secondary-900 text-white rounded-md hover:bg-black transition-colors disabled:opacity-50 text-xs font-bold"
                                        >
                                            {loadingCep ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Buscar'}
                                        </button>
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                    <div className="md:col-span-3 space-y-1">
                                        <label className="text-xs font-medium text-secondary-700">Rua/Logradouro</label>
                                        <input
                                            type="text"
                                            value={addressFields.street}
                                            onChange={(e) => handleAddressFieldChange('street', e.target.value)}
                                            className="input"
                                            placeholder="Nome da rua"
                                        />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-xs font-medium text-secondary-700">Número</label>
                                        <input
                                            type="text"
                                            value={addressFields.number}
                                            onChange={(e) => handleAddressFieldChange('number', e.target.value)}
                                            className="input"
                                            placeholder="123"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div className="space-y-1">
                                        <label className="text-xs font-medium text-secondary-700">Bairro</label>
                                        <input
                                            type="text"
                                            value={addressFields.neighborhood}
                                            onChange={(e) => handleAddressFieldChange('neighborhood', e.target.value)}
                                            className="input"
                                            placeholder="Bairro"
                                        />
                                    </div>
                                    <div className="grid grid-cols-3 gap-2">
                                        <div className="col-span-2 space-y-1">
                                            <label className="text-xs font-medium text-secondary-700">Cidade</label>
                                            <input
                                                type="text"
                                                value={addressFields.city}
                                                onChange={(e) => handleAddressFieldChange('city', e.target.value)}
                                                className="input"
                                                placeholder="Cidade"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <label className="text-xs font-medium text-secondary-700">UF</label>
                                            <input
                                                type="text"
                                                value={addressFields.state}
                                                onChange={(e) => handleAddressFieldChange('state', e.target.value)}
                                                className="input uppercase"
                                                maxLength={2}
                                                placeholder="SP"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Seção Identificação do Projeto */}
                        <div className="space-y-4 md:col-span-2 pt-2">
                            <h3 className="text-xs font-semibold uppercase tracking-wider text-secondary-500 flex items-center gap-2">
                                <span className="w-8 h-px bg-secondary-200"></span>
                                Identificação do Projeto
                            </h3>
                            <div className="space-y-1">
                                <label className="text-xs font-medium text-secondary-700">Nome do Projeto *</label>
                                <input
                                    type="text"
                                    name="project_name"
                                    value={formData.project_name}
                                    onChange={handleChange}
                                    className="input"
                                    placeholder="Ex: Cozinha Planejada - Ed. Solar"
                                    required
                                />
                            </div>
                        </div>

                        {/* Seção Horas por Setor */}
                        <div className="space-y-4 md:col-span-2 pt-2">
                            <ProjectHoursForm onHoursChange={handleHoursChange} />
                        </div>
                    </div>
                </form>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 p-4 border-t border-secondary-100 bg-secondary-50/50">
                    <button
                        type="button"
                        onClick={onClose}
                        className="btn-secondary px-6"
                    >
                        Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={loading}
                        className="btn-primary px-6"
                    >
                        {loading ? 'Criando...' : 'Criar Projeto'}
                    </button>
                </div>
            </div>

            {showQuickClient && (
                <QuickClientModal
                    onClose={() => setShowQuickClient(false)}
                    onSuccess={(newClient) => {
                        fetchClients(); // Refresh list
                        handleClientSelect(newClient); // Select the new client
                    }}
                />
            )}
        </div>
    );
};

export default ManualProjectModal;
